import { type JSX } from 'react';
import { useIntl } from 'react-intl';

import Breakpoints from '@dynatrace/strato-design-tokens/breakpoints-js';
import {
  ChevronLeftSmallIcon,
  ChevronRightSmallIcon,
} from '@dynatrace/strato-icons';

import { Button } from '../../../../buttons/button/Button.js';
import { useId } from '../../../../core/hooks/useId.js';
import { DataTestId } from '../../../../core/types/data-props.js';
import { StylingProps } from '../../../../core/types/styling-props.js';
import { Select } from '../../../../forms/select/Select.js';
import type { SelectSingleValue } from '../../../../forms/select/types.js';
import { Flex } from '../../../../layouts/flex/Flex.js';
import { useBreakpoint } from '../../../../layouts/hooks/useBreakpoint.js';
import { Text } from '../../../../typography/text/Text.js';
import { useDataTable } from '../../contexts/DataTableContext.js';
import { dataTableMessages } from '../../messages.js';

/**
 * DataTable internal pagination component that is actually rendering the pagination.
 * @internal
 */
interface DataTablePaginationInternalProps extends DataTestId, StylingProps {
  /**
   * The element's unique identifier.
   *  @see [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/id).
   */
  id?: string;
}

interface PaginationControlsProps {
  /**
   * Number of rows per page.
   */
  pageSize: number;
  /**
   *  Number that represents the number of available pages. Will be -1 for server-side pagination.
   */
  pageCount: number;
  /**
   * Current page index in a controlled scenario.
   */
  pageIndex: number;
  /**
   * Whether the "Go to previous page" button is disabled.
   */
  isPrevPageDisabled: boolean;
  /**
   * Whether the "Go to next page" button is disabled.
   */
  isNextPageDisabled: boolean;
  /**
   * Whether pagination is server-side controlled, derived from enablePreviousPage or enableNextPage pagination state flags
   */
  isServerSidePaginated: boolean;
  /**
   * Called when the page index changes. Provides the changed page index along with the underlying trigger.
   */
  onPageIndexChange: (pageIndex: number, trigger: 'user' | 'internal') => void;
  /**
   * The total number of rows.
   * Will be used to determine the total number of pages to display the indicator "Page X of Y" for server-side pagination.
   */
  totalRowsCount?: number;
}

/**
 * Page Select and next/prev buttons
 */
const PaginationNavigation = ({
  pageSize,
  pageCount,
  pageIndex,
  isPrevPageDisabled,
  isNextPageDisabled,
  isServerSidePaginated,
  totalRowsCount,
  onPageIndexChange,
}: PaginationControlsProps) => {
  const pageIndexSelectId = useId();
  const intl = useIntl();
  const smallScreen = useBreakpoint(`(max-width: ${Breakpoints.Max.Mobile}px)`);
  const totalPages =
    isServerSidePaginated && totalRowsCount !== undefined
      ? Math.ceil(totalRowsCount / pageSize)
      : pageCount;
  // An array of zero-based index integers corresponding to available pages in the table
  const pageCountArray: number[] = Array.from(
    { length: totalPages },
    (_, i) => i,
  );
  const hasMultiplePages = totalPages > 1;
  const shouldShowPageInfo =
    !isServerSidePaginated || (isServerSidePaginated && totalPages !== -1);

  const totalPagesCountText = hasMultiplePages
    ? intl.formatMessage(dataTableMessages.paginationTotalPages, { totalPages })
    : intl.formatMessage(dataTableMessages.paginationSinglePageInfo);

  const handlePageIndexSelect = (newPageIndex: SelectSingleValue<number>) =>
    newPageIndex !== null && onPageIndexChange(newPageIndex, 'user');

  const handlePreviousPage = () => {
    const prevPageIndex = Math.max(0, pageIndex - 1);
    onPageIndexChange(prevPageIndex, 'user');
  };

  const handleNextPage = () => onPageIndexChange(pageIndex + 1, 'user');

  return (
    <Flex gap={16} justifySelf="flex-end">
      {shouldShowPageInfo && (
        <Flex flexWrap="nowrap" alignItems="center">
          <Text as="p">
            {intl.formatMessage(dataTableMessages.paginationPageSelection)}
          </Text>
          {hasMultiplePages && (
            <Select
              id={pageIndexSelectId}
              value={pageIndex}
              onChange={handlePageIndexSelect}
              aria-label={intl.formatMessage(dataTableMessages.selectPage)}
              data-dt-name="PaginationPageIndex"
            >
              <Select.Content>
                {pageCountArray.length === 0 ? (
                  <Select.Option key="pageIndex-0" id={0} value={0}>
                    {0}
                  </Select.Option>
                ) : (
                  pageCountArray.map((pageIndexOption) => (
                    <Select.Option
                      key={`pageIndex-${pageIndexOption}`}
                      id={pageIndexOption}
                      value={pageIndexOption}
                    >
                      {pageIndexOption + 1}
                    </Select.Option>
                  ))
                )}
              </Select.Content>
            </Select>
          )}
          {(!hasMultiplePages || !smallScreen) && (
            <Text as="span" textStyle="base" style={{ whiteSpace: 'nowrap' }}>
              {totalPagesCountText}
            </Text>
          )}
        </Flex>
      )}
      {(!smallScreen || isServerSidePaginated) && (
        <Flex gap={2}>
          <Button
            onClick={handlePreviousPage}
            disabled={isPrevPageDisabled}
            aria-label={intl.formatMessage(
              dataTableMessages.paginationPrevPage,
            )}
            variant="default"
            data-dt-name="PaginationPreviousPage"
          >
            <Button.Prefix>
              <ChevronLeftSmallIcon />
            </Button.Prefix>
          </Button>
          <Button
            onClick={handleNextPage}
            disabled={isNextPageDisabled}
            aria-label={intl.formatMessage(
              dataTableMessages.paginationNextPage,
            )}
            variant="default"
            data-dt-name="PaginationNextPage"
          >
            <Button.Prefix>
              <ChevronRightSmallIcon />
            </Button.Prefix>
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

/**
 * Internal component that actually renders the pagination.
 * @internal
 */
export function DataTablePaginationInternal(
  props: Readonly<DataTablePaginationInternalProps>,
): JSX.Element {
  const table = useDataTable();
  const intl = useIntl();
  const pageSizeSelectId = useId();
  const paginationState = table.atoms.pagination?.get();
  // Number of rows per page
  const pageSize = paginationState.pageSize;
  // handler that is called when the page size changes
  const onPageSizeChange = table.setPageSize;
  const pageSizeOptions = table.options.availablePaginationOptions ?? [];
  // If there is only one entry in the pageSizeOptions, we do not render the select
  const hasPageSizeOptions = pageSizeOptions.length > 1;
  const { manualPagination } = table.options;
  const { enablePreviousPage, enableNextPage } = paginationState;
  const isServerSidePaginated =
    enableNextPage !== undefined || enablePreviousPage !== undefined;
  const isPrevPageDisabled = manualPagination
    ? !enablePreviousPage
    : !table.getCanPreviousPage();

  const isNextPageDisabled = manualPagination
    ? !enableNextPage
    : !table.getCanNextPage();

  const {
    id,
    'data-testid': dataTestId,
    className: consumerClassName,
    style: consumerStyle,
    ...remainingProps
  } = props;

  return (
    <Flex
      id={id}
      data-testid={dataTestId}
      className={consumerClassName}
      style={consumerStyle}
      justifyContent={hasPageSizeOptions ? 'space-between' : 'flex-end'}
      paddingTop={8}
      role="navigation"
      flexWrap="wrap"
      {...remainingProps}
    >
      {hasPageSizeOptions && (
        <Flex alignItems="center">
          <Select
            id={pageSizeSelectId}
            value={pageSize}
            required
            onChange={(newPageSize) =>
              newPageSize !== null && onPageSizeChange?.(newPageSize)
            }
            aria-label={intl.formatMessage(dataTableMessages.selectPageSize)}
            data-dt-name="PaginationPageSize"
          >
            <Select.Content>
              {pageSizeOptions.map((pageSize) => (
                <Select.Option
                  key={`${pageSize}`}
                  id={pageSize}
                  value={pageSize}
                >
                  {`${pageSize}`}
                </Select.Option>
              ))}
            </Select.Content>
          </Select>
          <Text as="span" style={{ whiteSpace: 'nowrap' }}>
            {intl.formatMessage(dataTableMessages.paginationRowsPerPage)}
          </Text>
        </Flex>
      )}

      <PaginationNavigation
        pageSize={pageSize}
        pageCount={table.getPageCount()}
        pageIndex={paginationState.pageIndex}
        isPrevPageDisabled={isPrevPageDisabled}
        isNextPageDisabled={isNextPageDisabled}
        isServerSidePaginated={isServerSidePaginated}
        totalRowsCount={paginationState.totalRowsCount}
        onPageIndexChange={table.setPageIndex}
      />
    </Flex>
  );
}
